import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useDeleteRequest } from '@/shared/hooks/requests.js';
import { SHARE_FILE } from "@/shared/utils/urls.js";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

export const DeleteShareModal = ({ open, setOpen, file, setData }) => {
  const { t } = useTranslation()
  // DELETE /embed/v1/share — single share, query params only (no body)
  const deleteShare = useDeleteRequest({ url: SHARE_FILE });

  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    if (open) setSelectedUser(null);
  }, [open]);

  // The recipients to choose from come straight off the share item
  const users = file?.sharedWith || file?.shared_with || [];

  const handleDelete = async () => {
    if (!selectedUser) return;

    // Unified API: every share is addressed by its node id.
    const nodeId = file?.node?.id || file?.id;

    const { success, error } = await deleteShare.request({
      params: {
        node_id: nodeId,
        shared_with_id: selectedUser,
      },
    });

    if (success) {
      toast.success(t('shareModal:share.success.unshared'));
      setOpen(false);
      setData()
    } else {
      toast.error(error || t('shareModal:share.error.generic'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('shareModal:remove.modal.title')}</DialogTitle>
        </DialogHeader>

        {/* min-h leaves room for the dropdown below the select; buttons stick
            to the bottom via mt-auto so the popover never overlaps them. */}
        <div className="p-2 min-h-[180px] flex flex-col gap-4">
          {/* Select user */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                {selectedUser
                  ? users.find((u) => u.id === selectedUser)?.email || t('shareModal:share.unknownUser')
                  : t('shareModal:remove.modal.selector')}
                <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <Command>
                <CommandList>
                  <CommandEmpty>{t('shareModal:share.noUsers')}</CommandEmpty>
                  <CommandGroup>
                    {users.map((user) => (
                      <CommandItem
                        key={user.id}
                        onSelect={() => setSelectedUser(user.id)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedUser === user.id
                              ? "opacity-100"
                              : "opacity-0"
                          )}
                        />
                        {user.email || "unknown"}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Buttons */}
          <div className="w-full mt-auto flex items-center justify-between gap-[15px]">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-black w-full hover:bg-gray-100"
            >
              {t('shareModal:remove.modal.btn.cancel')}
            </Button>
            <Button
              className="w-full"
              disabled={!selectedUser}
              onClick={handleDelete}
            >
              {t('shareModal:remove.modal.btn.submit')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
